import { useState, useEffect } from 'react';
import { settingsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';
import { 
  Plus,
  Pencil,
  Trash2,
  ChevronRight,
  FileText,
  Calculator,
  Layers,
  FolderTree,
  Hash,
  Search
} from 'lucide-react';

const CATEGORIES = ['Sales', 'Purchases', 'Overhead', 'Salaries', 'Management Fees', 'Other Expenses'];

const CATEGORY_COLORS = {
  'Sales': { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', icon: 'text-emerald-600' },
  'Purchases': { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400', icon: 'text-red-600' },
  'Overhead': { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', icon: 'text-amber-600' },
  'Salaries': { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', icon: 'text-blue-600' },
  'Management Fees': { bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400', icon: 'text-purple-600' },
  'Other Expenses': { bg: 'bg-slate-100 dark:bg-zinc-800', text: 'text-slate-700 dark:text-zinc-400', icon: 'text-slate-600' },
};

const AccountsTab = () => {
  const [loading, setLoading] = useState(true);
  const [accountsHierarchy, setAccountsHierarchy] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedParent, setSelectedParent] = useState('');
  const { isDark } = useTheme();

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const res = await settingsApi.getAccountsHierarchy();
      setAccountsHierarchy(res.data);
    } catch (error) {
      toast.error('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = (category = '', parentCode = '') => {
    setDialogMode('create');
    setSelectedAccount(null);
    setSelectedCategory(category);
    setSelectedParent(parentCode);
    setDialogOpen(true);
  };

  const openEditDialog = (account) => {
    setDialogMode('edit');
    setSelectedAccount(account);
    setSelectedCategory(account.category);
    setSelectedParent(account.parent_code || '');
    setDialogOpen(true);
  };

  const handleSave = async (formData) => {
    try {
      if (dialogMode === 'create') {
        await settingsApi.createAccount(formData);
        toast.success('Account created successfully');
      } else {
        await settingsApi.updateAccount(selectedAccount.id, formData);
        toast.success('Account updated successfully');
      }
      await loadAccounts();
      setDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save account');
    }
  };

  const handleDelete = async (account) => {
    if (!window.confirm(`Are you sure you want to delete "${account.code} - ${account.name}"?`)) return;
    
    try {
      await settingsApi.deleteAccount(account.id);
      toast.success('Account deleted');
      await loadAccounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete account');
    }
  };

  // Get all main accounts for parent selection
  const getAllMainAccounts = () => {
    const mainAccounts = [];
    Object.entries(accountsHierarchy).forEach(([cat, data]) => {
      data.main_accounts?.forEach(acc => {
        if (acc.account_type === 'main_account') {
          mainAccounts.push({ ...acc, category: cat });
        }
      });
    });
    return mainAccounts;
  };

  // Filter accounts based on search
  const filterHierarchy = () => {
    if (!searchTerm) return accountsHierarchy;
    
    const filtered = {};
    const term = searchTerm.toLowerCase();
    
    Object.entries(accountsHierarchy).forEach(([cat, data]) => {
      const filteredMain = data.main_accounts?.filter(main => {
        const mainMatch = main.code.toLowerCase().includes(term) || 
                         main.name.toLowerCase().includes(term);
        const subMatch = main.sub_accounts?.some(sub => 
          sub.code.toLowerCase().includes(term) || 
          sub.name.toLowerCase().includes(term)
        );
        return mainMatch || subMatch;
      }).map(main => ({
        ...main,
        sub_accounts: main.sub_accounts?.filter(sub =>
          sub.code.toLowerCase().includes(term) || 
          sub.name.toLowerCase().includes(term) ||
          main.code.toLowerCase().includes(term) ||
          main.name.toLowerCase().includes(term)
        )
      }));
      
      if (filteredMain?.length > 0) {
        filtered[cat] = { main_accounts: filteredMain, total_accounts: data.total_accounts };
      }
    });
    
    return filtered;
  };

  const filteredHierarchy = filterHierarchy();

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full rounded-sm" />
        <Skeleton className="h-64 rounded-sm" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className={`text-xl font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
            Chart of Accounts
          </h2>
          <p className={`text-sm mt-1 ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
            Manage account hierarchy for budget templates. Changes here will be reflected globally.
          </p>
        </div>
        <Button 
          onClick={() => openCreateDialog()}
          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
          data-testid="add-account-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Account
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`} />
        <Input
          placeholder="Search by account code or name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`pl-10 rounded-sm ${isDark ? 'bg-zinc-800 border-zinc-700' : ''}`}
        />
      </div>

      {/* Category Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {CATEGORIES.map(cat => {
          const colors = CATEGORY_COLORS[cat];
          const data = accountsHierarchy[cat] || { total_accounts: 0 };
          return (
            <Card 
              key={cat}
              className={`border shadow-none rounded-sm cursor-pointer transition-all hover:scale-[1.02] ${
                isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'
              }`}
              onClick={() => {
                const element = document.getElementById(`category-${cat.replace(/\s+/g, '-')}`);
                element?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              <CardContent className="p-3">
                <div className={`w-8 h-8 rounded-sm flex items-center justify-center mb-2 ${colors.bg}`}>
                  <Layers className={`w-4 h-4 ${colors.icon}`} />
                </div>
                <p className={`text-xs font-medium truncate ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                  {cat}
                </p>
                <p className={`font-mono text-lg font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                  {data.total_accounts}
                </p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Accounts Hierarchy */}
      <div className="space-y-4">
        {Object.entries(filteredHierarchy).map(([category, data]) => {
          const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS['Other Expenses'];
          return (
            <Card 
              key={category}
              id={`category-${category.replace(/\s+/g, '-')}`}
              className={`border shadow-none rounded-sm ${isDark ? 'bg-zinc-900 border-zinc-800' : 'border-slate-200'}`}
            >
              <CardHeader className={`border-b ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${colors.bg}`}>
                      <FolderTree className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <div>
                      <CardTitle className="font-display text-lg font-medium">{category}</CardTitle>
                      <p className={`text-sm ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                        {data.total_accounts} accounts
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => openCreateDialog(category)}
                    className="rounded-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add to {category}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {data.main_accounts?.length > 0 ? (
                  <Accordion type="multiple" className="w-full">
                    {data.main_accounts.map((mainAccount) => (
                      <AccordionItem 
                        key={mainAccount.id} 
                        value={mainAccount.code}
                        className={`border-b last:border-b-0 ${isDark ? 'border-zinc-800' : 'border-slate-100'}`}
                      >
                        <AccordionTrigger className={`px-4 py-3 hover:no-underline ${isDark ? 'hover:bg-zinc-800/50' : 'hover:bg-slate-50'}`}>
                          <div className="flex items-center gap-3 flex-1">
                            <Badge className={`font-mono ${colors.bg} ${colors.text}`}>
                              {mainAccount.code}
                            </Badge>
                            <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                              {mainAccount.name}
                            </span>
                            {mainAccount.is_header && (
                              <Badge variant="outline" className="text-xs">Header</Badge>
                            )}
                            <span className={`text-sm ml-auto mr-4 ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                              {mainAccount.sub_accounts?.length || 0} sub-accounts
                            </span>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent>
                          <div className={`mx-4 mb-4 rounded-sm ${isDark ? 'bg-zinc-800/50' : 'bg-slate-50'}`}>
                            {/* Main Account Actions */}
                            <div className={`px-4 py-2 flex items-center justify-end gap-2 border-b ${isDark ? 'border-zinc-700' : 'border-slate-200'}`}>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openCreateDialog(category, mainAccount.code)}
                                className="text-xs"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add Sub-Account
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => openEditDialog(mainAccount)}
                              >
                                <Pencil className="w-3 h-3" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleDelete(mainAccount)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                            
                            {/* Sub-accounts list */}
                            {mainAccount.sub_accounts?.length > 0 ? (
                              <div className={`divide-y ${isDark ? 'divide-zinc-700' : 'divide-slate-200'}`}>
                                {mainAccount.sub_accounts.map((sub) => (
                                  <div 
                                    key={sub.id}
                                    className={`px-4 py-3 flex items-center justify-between ${isDark ? 'hover:bg-zinc-700/50' : 'hover:bg-white'}`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <ChevronRight className={`w-4 h-4 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                                      <span className={`font-mono text-sm ${isDark ? 'text-zinc-400' : 'text-slate-500'}`}>
                                        {sub.code}
                                      </span>
                                      <span className={isDark ? 'text-zinc-300' : 'text-slate-700'}>
                                        {sub.name}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7"
                                        onClick={() => openEditDialog(sub)}
                                      >
                                        <Pencil className="w-3 h-3" />
                                      </Button>
                                      <Button 
                                        variant="ghost" 
                                        size="icon" 
                                        className="h-7 w-7 text-red-600"
                                        onClick={() => handleDelete(sub)}
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                                No sub-accounts. Click "Add Sub-Account" to create one.
                              </div>
                            )}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                ) : (
                  <div className={`p-8 text-center ${isDark ? 'text-zinc-500' : 'text-slate-400'}`}>
                    <Calculator className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-zinc-600' : 'text-slate-300'}`} />
                    <p>No accounts in this category</p>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-3 rounded-sm"
                      onClick={() => openCreateDialog(category)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add First Account
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Account Form Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Add New Account' : 'Edit Account'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'create' 
                ? 'Create a new account that will be available in budget templates.'
                : 'Update the account details.'
              }
            </DialogDescription>
          </DialogHeader>
          <AccountForm
            account={selectedAccount}
            category={selectedCategory}
            parentCode={selectedParent}
            mainAccounts={getAllMainAccounts()}
            onSave={handleSave}
            onCancel={() => setDialogOpen(false)}
            isDark={isDark}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AccountForm = ({ account, category, parentCode, mainAccounts, onSave, onCancel, isDark }) => {
  const [code, setCode] = useState(account?.code || '');
  const [name, setName] = useState(account?.name || '');
  const [selectedCategory, setSelectedCategory] = useState(account?.category || category || '');
  const [accountType, setAccountType] = useState(account?.account_type || (parentCode ? 'sub_account' : 'main_account'));
  const [selectedParent, setSelectedParent] = useState(account?.parent_code || parentCode || '');
  const [isHeader, setIsHeader] = useState(account?.is_header || false);
  const [sortOrder, setSortOrder] = useState(account?.sort_order || 0);

  // Filter main accounts by selected category
  const categoryMainAccounts = mainAccounts.filter(acc => acc.category === selectedCategory);

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!code || !name || !selectedCategory) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Auto-calculate sort order if not set
    let finalSortOrder = sortOrder;
    if (!finalSortOrder && selectedParent) {
      const parent = mainAccounts.find(m => m.code === selectedParent);
      if (parent) {
        finalSortOrder = parent.sort_order + 1;
      }
    }

    onSave({
      code,
      name,
      category: selectedCategory,
      account_type: accountType,
      parent_code: accountType === 'sub_account' ? selectedParent : null,
      is_header: accountType === 'main_account' ? isHeader : false,
      sort_order: finalSortOrder
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Account Code *</Label>
          <Input 
            value={code} 
            onChange={(e) => setCode(e.target.value)} 
            placeholder="e.g., 402037"
            required 
            className="rounded-sm font-mono" 
          />
        </div>
        <div className="space-y-2">
          <Label>Sort Order</Label>
          <Input 
            type="number"
            value={sortOrder} 
            onChange={(e) => setSortOrder(Number(e.target.value))} 
            placeholder="0"
            className="rounded-sm" 
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Account Name *</Label>
        <Input 
          value={name} 
          onChange={(e) => setName(e.target.value)} 
          placeholder="e.g., Salaries - Recoveries"
          required 
          className="rounded-sm" 
        />
      </div>

      <div className="space-y-2">
        <Label>Category *</Label>
        <Select value={selectedCategory} onValueChange={(v) => {
          setSelectedCategory(v);
          setSelectedParent(''); // Reset parent when category changes
        }}>
          <SelectTrigger className="rounded-sm" data-testid="category-select">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Account Type *</Label>
        <Select value={accountType} onValueChange={setAccountType}>
          <SelectTrigger className="rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="main_account">Main Account (Header)</SelectItem>
            <SelectItem value="sub_account">Sub-Account (Has Figures)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {accountType === 'sub_account' && (
        <div className="space-y-2">
          <Label>Parent Account *</Label>
          <Select value={selectedParent} onValueChange={setSelectedParent}>
            <SelectTrigger className="rounded-sm">
              <SelectValue placeholder="Select parent account" />
            </SelectTrigger>
            <SelectContent>
              {categoryMainAccounts.map(acc => (
                <SelectItem key={acc.code} value={acc.code}>
                  {acc.code} - {acc.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {categoryMainAccounts.length === 0 && (
            <p className="text-xs text-amber-600">
              No main accounts in this category. Create a main account first.
            </p>
          )}
        </div>
      )}

      {accountType === 'main_account' && (
        <div className="flex items-center space-x-2">
          <Switch
            id="is-header"
            checked={isHeader}
            onCheckedChange={setIsHeader}
          />
          <Label htmlFor="is-header" className="text-sm">
            This is a header account (sums sub-accounts, no direct figures)
          </Label>
        </div>
      )}

      <div className={`p-3 rounded-sm text-sm ${isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-slate-50 text-slate-500'}`}>
        <p className="font-medium mb-1">How accounts work in budgets:</p>
        <ul className="list-disc list-inside space-y-1">
          <li><strong>Main Account (Header):</strong> Groups sub-accounts, shows totals</li>
          <li><strong>Sub-Account:</strong> Where actual budget figures are entered</li>
        </ul>
      </div>

      <DialogFooter className="gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="rounded-sm">
          Cancel
        </Button>
        <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">
          {account ? 'Save Changes' : 'Create Account'}
        </Button>
      </DialogFooter>
    </form>
  );
};

export default AccountsTab;
